import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Brain, Sparkles, Power } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

export const NlpSettingsCard = () => {
    const [nlpSettings, setNlpSettings] = useState({ enabled: false, auto_analyze: true });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/nlp/settings').then(res => setNlpSettings(res.data)).catch(e => logger.warn('NLP settings fetch error', e)).finally(() => setLoading(false));
    }, []);

    const handleToggle = async (enabled) => {
        setSaving(true);
        try {
            const res = await api.put('/nlp/settings', { ...nlpSettings, enabled });
            setNlpSettings(res.data);
            toast.success(enabled ? 'NLP analysis enabled' : 'NLP analysis disabled');
        } catch { toast.error('Failed to update NLP settings'); }
        finally { setSaving(false); }
    };

    const handleToggleAutoAnalyze = async (auto_analyze) => {
        setSaving(true);
        try {
            const res = await api.put('/nlp/settings', { ...nlpSettings, auto_analyze });
            setNlpSettings(res.data);
            toast.success(auto_analyze ? 'Auto-analyze enabled' : 'Auto-analyze disabled');
        } catch { toast.error('Failed to update NLP settings'); }
        finally { setSaving(false); }
    };

    if (loading) return null;

    return (
        <Card className={`border-2 transition-colors ${nlpSettings.enabled ? 'border-purple-500/50 bg-purple-500/5' : 'border-muted'}`} data-testid="nlp-settings-card">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Brain className="h-5 w-5 text-purple-500" />
                        <CardTitle>AI Call Analysis (NLP)</CardTitle>
                        {nlpSettings.enabled
                            ? <Badge className="bg-purple-500 text-white text-[10px]">Active</Badge>
                            : <Badge variant="outline" className="text-muted-foreground text-[10px]">Off</Badge>
                        }
                    </div>
                    <div className="flex items-center gap-3">
                        {saving && <Loader2 className="h-4 w-4 animate-spin text-purple-500" />}
                        <Switch checked={nlpSettings.enabled} onCheckedChange={handleToggle} disabled={saving} data-testid="nlp-master-toggle" />
                    </div>
                </div>
                <CardDescription>
                    Automatically transcribe call recordings using AI (Whisper) and get agent coaching insights (Claude).
                    <span className="block mt-1 text-amber-600 font-medium text-xs">Cost: Each analysis uses API credits. Toggle off to stop spending.</span>
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-background border">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-purple-500" />
                                <span className="text-sm font-medium">Auto-Analyze New Calls</span>
                            </div>
                            <Switch checked={nlpSettings.auto_analyze} onCheckedChange={handleToggleAutoAnalyze} disabled={saving || !nlpSettings.enabled} data-testid="nlp-auto-analyze-toggle" />
                        </div>
                        <p className="text-xs text-muted-foreground">When enabled, every incoming 3CX call with a recording is automatically transcribed and analyzed.</p>
                    </div>
                    <div className="p-4 rounded-lg bg-background border">
                        <div className="flex items-center gap-2 mb-2">
                            <Power className="h-4 w-4 text-purple-500" />
                            <span className="text-sm font-medium">Status</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {nlpSettings.enabled
                                ? `Enabled by ${nlpSettings.updated_by_name || 'System'} on ${nlpSettings.updated_at ? new Date(nlpSettings.updated_at).toLocaleString() : 'N/A'}`
                                : 'NLP analysis is currently off. No API credits are being used.'
                            }
                        </p>
                    </div>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 border text-xs text-muted-foreground space-y-1">
                    <p className="font-medium text-foreground">How it works:</p>
                    <p>1. A call ends and 3CX sends the recording URL via webhook</p>
                    <p>2. OpenAI Whisper transcribes the audio (supports English, Malayalam, Tamil, Hindi)</p>
                    <p>3. Claude AI analyzes the transcript for sentiment, agent quality, and coaching tips</p>
                    <p>4. Results appear in the call history inside any CRM contact modal</p>
                </div>
            </CardContent>
        </Card>
    );
};
